import { createFormatter } from "@kovernow/flows/format";
import {
  canChoosePlan,
  DEFAULT_PLAN_TERM,
  payRoute,
  quoteQueryKeys,
  selectedPlan,
  type Quote,
} from "@kovernow/flows/quote";
import { toRecoveryAction } from "@kovernow/flows/recovery";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { RadioGroup } from "@kovernow/react-ui/components/radio-group";
import { Skeleton } from "@kovernow/react-ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { GatedButton } from "../../components/GatedButton";
import { Page } from "../../components/Page";
import { Recovery } from "../../components/Recovery";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { isNotFound, ItemNotFound, useItem, useItemName, useItemValue } from "../item-detail/shared";
import { useValidateGate, useValidateRecoveryHandlers } from "../item-detail/validate-gate";
import { CoverageSummary } from "./CoverageSummary";
import { PlanCard } from "./PlanCard";
import { PriceSummary } from "./PriceSummary";

/**
 * W-10 Choose plan. `POST /pricing` returns the server-priced plans (the
 * server picks the underwriter: QBE 3/6/12 or AXA 12). `?term=` holds the
 * selected plan (default 12 months); selecting saves nothing. "Continue to
 * pay" runs `POST /items/validate` (the same recovery as the item detail),
 * then `POST /quotations {prospectId, plan}`, then opens Pay (W-11).
 */
export function ChoosePlan() {
  const t = useT();
  const { itemId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { quoteApi, config } = useServices();
  const nameOf = useItemName();
  const valueOf = useItemValue();

  const itemQ = useItem(itemId);
  const item = itemQ.data;
  const allowed = !!item && canChoosePlan(item);
  const pricingQ = useQuery({
    queryKey: quoteQueryKeys.pricing(itemId),
    queryFn: () => quoteApi.price(itemId),
    enabled: allowed,
    retry: false,
    // Prices move (discounts, credit, depreciation): always ask the server.
    staleTime: 0,
    gcTime: 0,
  });
  const quotes = pricingQ.data;
  const plan = quotes ? selectedPlan(params.get("term"), quotes) : undefined;
  const quote = quotes?.find((q) => q.plan === plan);

  const gate = useValidateGate(item, {
    onProceed: async () => {
      if (!plan) return;
      const stored = await quoteApi.create({ itemId, plan });
      navigate(payRoute(itemId, stored._id));
    },
  });
  const recoveryHandlers = useValidateRecoveryHandlers(itemId, {
    verifyReturnTo: `/vault/${encodeURIComponent(itemId)}/plan?term=${plan ?? DEFAULT_PLAN_TERM}`,
  });

  const money = useMoney(quote);

  if (itemQ.isPending) return <ChoosePlanSkeleton />;
  if (!item) {
    if (isNotFound(itemQ.error)) return <ItemNotFound />;
    return (
      <ChoosePlanPage>
        <div data-testid="plan-error">
          <Recovery action={toRecoveryAction(itemQ.error)} onRetry={() => void itemQ.refetch()} retryable />
        </div>
      </ChoosePlanPage>
    );
  }
  // Insured, pending, rejected or archived: there is no plan to choose.
  if (!allowed) return <Navigate to={`/vault/${encodeURIComponent(itemId)}`} replace />;

  const name = nameOf(item);
  const value = valueOf(item);
  const underwriter = quote ? config.underwriters?.[quote.underwriter.toLowerCase()] : undefined;

  const select = (next: string) => {
    const p = new URLSearchParams(params);
    p.set("term", next);
    setParams(p, { replace: true });
    gate.setView(null);
  };

  return (
    <ChoosePlanPage>
      <div className="flex flex-col gap-6" data-testid="choose-plan">
        <div className="flex flex-col gap-1">
          <p className="text-lg font-semibold">{name}</p>
          {value ? <p className="text-sm text-muted-foreground">{t("choosePlan.itemValue", { value })}</p> : null}
        </div>

        {pricingQ.isPending ? (
          <PlansSkeleton />
        ) : pricingQ.isError ? (
          <div data-testid="pricing-error">
            <Recovery action={toRecoveryAction(pricingQ.error)} onRetry={() => void pricingQ.refetch()} retryable />
          </div>
        ) : !quote ? (
          <InfoBanner tone="info" title={t("choosePlan.noPlans")} data-testid="no-plans" />
        ) : (
          <>
            <section aria-labelledby="plan-length" className="flex flex-col gap-3">
              <h2 id="plan-length" className="text-xl font-semibold">
                {t("choosePlan.lengthHeading")}
              </h2>
              <RadioGroup aria-labelledby="plan-length" value={plan} onValueChange={select} className="gap-3" data-testid="plans">
                {quotes!.map((q) => (
                  <PlanCard key={q.plan} quote={q} selected={q.plan === plan} money={money} underwriterName={underwriter?.name} />
                ))}
              </RadioGroup>
            </section>

            <CoverageSummary coverage={underwriter?.coverage} classification={item.classification} />

            <PriceSummary quote={quote} money={money} />

            <div className="flex flex-col gap-3" data-testid="plan-actions">
              <div className="sm:max-w-64">
                <GatedButton variant="cta" label={t("choosePlan.continue")} busy={gate.busy} onClick={() => void gate.run()} />
              </div>
              <p role="status" className="text-sm text-muted-foreground">
                {gate.busy ? t("choosePlan.checking") : ""}
              </p>
              {gate.view?.kind === "recovery" ? (
                <div data-testid="continue-recovery" data-action={gate.view.action.action}>
                  <Recovery
                    action={gate.view.action}
                    message={gate.view.message}
                    onRetry={() => void gate.run()}
                    handlers={recoveryHandlers(gate.view.target)}
                  />
                </div>
              ) : null}
              {gate.view?.kind === "info" ? (
                <div role="status" aria-live="polite">
                  <InfoBanner tone="info" title={gate.view.title} body={gate.view.message} data-testid="continue-info" />
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </ChoosePlanPage>
  );
}

function ChoosePlanPage({ children }: { children: ReactNode }) {
  return (
    <Page titleKey="nav.choosePlan" headingKey="choosePlan.heading" flowKey="nav.vault">
      {children}
    </Page>
  );
}

/** Amounts in the quote's currency (the tenant's unless the server says otherwise). */
function useMoney(quote: Pick<Quote, "pricing"> | undefined) {
  const { config, format } = useServices();
  const currency = quote?.pricing.currency;
  return useMemo(
    () =>
      !currency || currency === config.currency
        ? format
        : createFormatter({ locale: config.locale, currency, timeZone: config.timeZone }),
    [currency, config.currency, config.locale, config.timeZone, format],
  );
}

function PlansSkeleton() {
  const t = useT();
  return (
    <div aria-busy="true" className="flex flex-col gap-3" data-testid="plans-skeleton">
      <p role="status" className="sr-only">
        {t("choosePlan.loading")}
      </p>
      <Skeleton className="h-7 w-2/3" />
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

function ChoosePlanSkeleton() {
  return (
    <ChoosePlanPage>
      <div className="flex flex-col gap-6">
        <Skeleton className="h-6 w-1/2" />
        <PlansSkeleton />
      </div>
    </ChoosePlanPage>
  );
}
